import {
  FulfillmentChannel,
  getDefaultTimezone,
  getFulfillmentChannelInitialValues,
  getPaymentMethodInitialValues,
  getRequiresActivationWithLimitsInitialValues,
  transformAwardRestrictionsToFormValues,
  transformOfferToFulfillmentWindowFormValue,
} from '@occc/feat-constraints';
import {
  LimitTimePeriod,
  getAwardCountInitialValues,
  getProgressPerPeriodLimitInitialValues,
  getProgressPerTransactionLimitInitialValues,
  hasProgressPerPeriodLimit,
  hasProgressPerTransactionLimit,
} from '@occc/feat-limits';
import { Earning_Mechanism_Product_Counting_Attribute, OfferFragment } from '@occc/graphql';
import { AwardType } from '@occc-app/offerHelpers/types';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';

import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import { getOfferFulfillmentPriorityInitialValues } from '@occc-app/offerHelpers/utils/getOfferFulfillmentPriorityInitialValues';
import {
  PunchcardConfigureFormInitialValues,
  PunchcardConfigureFormValues,
  PunchcardEarningMechanismInitialValues,
} from '../../../types';
import { transformCustomData } from '../../../../CampaignComponents/components/CustomData/utils';

const transformOfferEarningMechanism = (
  earningMechanism: OfferFragment['earningMechanism'],
): PunchcardEarningMechanismInitialValues => {
  if (!earningMechanism || !earningMechanismHelper.isVarietyPurchaseOverTime(earningMechanism)) {
    return {};
  }

  const punchcardChallenge = {
    earningMechanism: 'varietyPurchaseOverTime' as const,
    products: earningMechanism.products.map(({ purchaseThreshold, countProductsBy, ...rest }) => ({
      ...rest,
      countProductsBy,
      purchaseThreshold,
    })),
    stayVisibleWhileInProgress: {
      isEnabled: !!earningMechanism.stayVisibleWhileInProgress,
    },
  };

  const award = earningMechanism.varietyPurchaseOverTimeAward;

  if (awardHelper.isPointsAward(award)) {
    return {
      punchcardChallenge,
      awardType: AwardType.AwardPoints,
      awardPoints: { points: award.points },
    };
  }

  return {
    punchcardChallenge,
    awardType: AwardType.AwardVouchers,
    awardVouchers: award.voucherExpirationDays
      ? {
          voucheredOffer: award.voucheredOffer,
          voucherExpirationDays: award.voucherExpirationDays,
          isEnabled: true,
        }
      : { voucheredOffer: award.voucheredOffer, isEnabled: false },
  };
};

const getOfferConfigureFormInitialValues = (): PunchcardConfigureFormInitialValues => ({
  earningMechanism: {},
  awardVouchers: {
    isEnabled: false,
  },
  awardType: AwardType.AwardPoints,
  awardCount: {
    isEnabled: false,
    global: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    membership: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    transactionFrequency: {
      isEnabled: false,
      rows: [{ limit: 1, timePeriod: LimitTimePeriod.Day }],
    },
  },
  punchcardChallenge: {
    products: [
      { countProductsBy: Earning_Mechanism_Product_Counting_Attribute.Quantity },
    ] as PunchcardConfigureFormValues['punchcardChallenge']['products'],
    earningMechanism: 'varietyPurchaseOverTime',
  },
  activation: {
    isEnabled: false,
    global: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    membership: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    requiresActivation: {
      isMustFulfillWithinEnabled: false,
      allowMultipleFulfillmentsOnActivation: false,
    },
  },
  fulfillmentWindow: {
    isEnabled: false,
    timezone: getDefaultTimezone(),
  },
  paymentMethodConstraint: {
    isEnabled: false,
  },
  fulfillmentChannelConstraint: {
    isEnabled: false,
    channel: FulfillmentChannel.OrderAheadOnly,
  },
  awardRestrictions: { isEnabled: false, awards: [] },
});

const getAwardCountSchemaInitialValues = (
  offer: OfferFragment,
): NonNullable<PunchcardConfigureFormInitialValues['awardCount']> => {
  const awardCount = getAwardCountInitialValues(offer, undefined, { excludeTotal: true });

  return {
    ...awardCount,
    isEnabled:
      awardCount.isEnabled ||
      hasProgressPerTransactionLimit(offer.limits) ||
      hasProgressPerPeriodLimit(offer.limits),
    progressPerPeriod: getProgressPerPeriodLimitInitialValues(offer),
    progressPerTransaction: getProgressPerTransactionLimitInitialValues(offer),
  } as NonNullable<PunchcardConfigureFormInitialValues['awardCount']>;
};

export const transformOfferToInitialValues = (
  offer?: OfferFragment,
): PunchcardConfigureFormInitialValues => {
  const values = getOfferConfigureFormInitialValues();

  if (!offer) {
    return values;
  }

  const awardRestrictionsConstraint = offer.constraints.find(
    (constraint) => constraint.__typename === 'ConstraintPurchasableEligibility',
  );

  return {
    ...values,
    ...getOfferNameAndDescriptionInitialValues(offer),
    ...getOfferFulfillmentPriorityInitialValues(offer),
    customData: transformCustomData(offer.customData),
    earningMechanism: {
      id: offer.earningMechanism?.id,
    },
    ...transformOfferEarningMechanism(offer.earningMechanism),
    activation: getRequiresActivationWithLimitsInitialValues(offer, values.activation),
    awardCount: getAwardCountSchemaInitialValues(offer),
    ...transformOfferToFulfillmentWindowFormValue(offer),
    paymentMethodConstraint: getPaymentMethodInitialValues(offer),
    fulfillmentChannelConstraint: getFulfillmentChannelInitialValues(offer),
    awardRestrictions: awardRestrictionsConstraint
      ? transformAwardRestrictionsToFormValues(awardRestrictionsConstraint)
      : values.awardRestrictions,
  };
};
